import { useAuthStore } from "@/stores/auth";
import { API_BASE_URL } from "@/api/client";
import type { AttachmentRef } from "@/types/api";

export type SSEEvent =
  | { event: "status"; data: { phase: string } }
  | { event: "token"; data: { text: string } }
  | { event: "tool_call"; data: { tool: string; args: Record<string, unknown> } }
  | { event: "tool_result"; data: { tool: string; summary: string } }
  | { event: "phase_change"; data: { to: string; document_id: number | null } }
  | { event: "error"; data: { message: string } }
  | { event: "done"; data: Record<string, never> };

export interface ChatRequest {
  content: string;
  attachments?: AttachmentRef[];
  model?: string;
}

/** Stream the chat response. Caller iterates the async generator until `done`. */
export async function* streamChat(
  conversationId: number,
  req: ChatRequest,
  signal?: AbortSignal,
): AsyncGenerator<SSEEvent, void, void> {
  const auth = useAuthStore();
  const resp = await fetch(`${API_BASE_URL}/conversations/${conversationId}/messages/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${auth.token ?? ""}`,
      Accept: "text/event-stream",
      "Cache-Control": "no-cache",
    },
    body: JSON.stringify({
      content: req.content,
      attachments: req.attachments ?? [],
      model: req.model,
    }),
    cache: "no-store",
    signal,
  });

  if (!resp.ok || !resp.body) {
    throw new Error(`stream failed: ${resp.status} ${resp.statusText}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE 规范允许 \r\n / \r / \n 作为行结束符；事件之间是连续两个空行。
    const SEP_RE = /\r\n\r\n|\r\r|\n\n/;
    let match: RegExpMatchArray | null;
    while ((match = SEP_RE.exec(buffer)) !== null) {
      const idx = match.index!;
      const rawEvent = buffer.slice(0, idx);
      buffer = buffer.slice(idx + match[0].length);
      const parsed = parseEvent(rawEvent);
      if (parsed) yield parsed;
    }
  }
}

function parseEvent(raw: string): SSEEvent | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split(/\r\n|\r|\n/)) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  if (!dataLines.length) return null;
  let data: any;
  try {
    data = JSON.parse(dataLines.join("\n"));
  } catch {
    return null;
  }
  return { event, data } as SSEEvent;
}
